/**
 * useScrollbarTheme — Web
 *
 * Watches the active client theme in Redux and updates the CSS custom property
 * `--scrollbar-thumb` on `:root` whenever the theme changes.
 *
 * The static `index.web.css` references `var(--scrollbar-thumb)` for all
 * `::-webkit-scrollbar-thumb` and Firefox `scrollbar-color` rules, so changing
 * this single CSS variable instantly re-colours every scrollbar in the app.
 *
 * Token used: `tokens.primarySoft` — the soft/pastel tint between primaryTintMed
 * and primaryBorder.  For the blue theme this is `#99C7FF` (as per Figma spec).
 *
 * Usage: call once at the root of the web app (AppNavigator.web.tsx):
 *   useScrollbarTheme();
 */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getClientThemeTokens } from '../theme/clientThemes';
import type { RootState } from '../store';

export function useScrollbarTheme(): void {
  const themeName = useSelector(
    (state: RootState) => state.theme?.themeName ?? 'blue',
  );

  useEffect(() => {
    const tokens = getClientThemeTokens(themeName);
    document.documentElement.style.setProperty(
      '--scrollbar-thumb',
      tokens.primarySoft,
    );
  }, [themeName]);
}